/**
 * Mentions are stored in message content as `<@userId>` tokens so they survive
 * username changes. The composer converts `@username` into tokens before sending.
 */
export const MENTION_TOKEN_RE = /<@([a-z0-9]{8,40})>/g;

export function extractMentionIds(content: string): string[] {
  const ids = new Set<string>();
  for (const match of content.matchAll(MENTION_TOKEN_RE)) ids.add(match[1]!);
  return [...ids];
}

/**
 * Replaces `@username` occurrences with `<@id>` tokens for users in the lookup.
 * Usernames are matched case-insensitively and must be bounded by whitespace/punctuation.
 */
export function encodeMentions(content: string, lookup: (username: string) => string | undefined): string {
  return content.replace(/(^|[\s(\[{"'])@([a-zA-Z0-9_.]{2,32})/g, (full, prefix: string, username: string) => {
    // A trailing period is almost always sentence punctuation, not part of the name.
    let name = username;
    let suffix = '';
    while (name.endsWith('.')) {
      name = name.slice(0, -1);
      suffix += '.';
    }
    const id = lookup(name.toLowerCase());
    return id ? `${prefix}<@${id}>${suffix}` : full;
  });
}

/** Role mentions: `<@&roleId>`. */
export const ROLE_MENTION_TOKEN_RE = /<@&([a-z0-9]{8,40})>/g;

export function extractRoleMentionIds(content: string): string[] {
  const ids = new Set<string>();
  for (const match of content.matchAll(ROLE_MENTION_TOKEN_RE)) ids.add(match[1]!);
  return [...ids];
}

/**
 * Replaces `@Role Name` with `<@&id>` tokens for the given roles (names may
 * contain spaces; the longest name wins). Case-insensitive, bounded like usernames.
 */
export function encodeRoleMentions(content: string, roles: ReadonlyArray<{ id: string; name: string }>): string {
  const sorted = [...roles].filter((r) => r.name.trim()).sort((a, b) => b.name.length - a.name.length);
  if (sorted.length === 0) return content;
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, (c) => `\\${c}`);
  const byName = new Map(sorted.map((r) => [r.name.toLowerCase(), r.id]));
  const re = new RegExp(`(^|[\\s(\\[{"'])@(${sorted.map((r) => escape(r.name)).join('|')})(?=$|[\\s.,!?;:)\\]}'"])`, 'gi');
  return content.replace(re, (full, prefix: string, name: string) => {
    const id = byName.get(name.toLowerCase());
    return id ? `${prefix}<@&${id}>` : full;
  });
}

/**
 * `@everyone` in a message: notifies everyone who can see the channel, when
 * the author may (Mention @everyone). Bounded like other mentions, and may sit
 * inside formatting (**@everyone**).
 */
const EVERYONE_RE = /(?:^|[\s(\[{"'*_~|>])@everyone(?![A-Za-z0-9_])/;

export function mentionsEveryone(content: string): boolean {
  return EVERYONE_RE.test(content);
}

/** Custom server emoji: `<:name:id>` (`<a:name:id>` is accepted too). */
export const CUSTOM_EMOJI_RE = /<a?:([A-Za-z0-9_]{2,32}):([a-z0-9]{8,40})>/g;
export const CUSTOM_EMOJI_EXACT_RE = /^<a?:([A-Za-z0-9_]{2,32}):([a-z0-9]{8,40})>$/;

/**
 * Replaces `<@id>`, `<@&id>` and `<:name:id>` tokens with readable text for
 * plain-text contexts (previews, notifications).
 */
export function renderMentionsAsText(content: string, resolve: (id: string) => string | undefined, resolveRole?: (id: string) => string | undefined): string {
  return content
    .replace(MENTION_TOKEN_RE, (_full, id: string) => `@${resolve(id) ?? 'unknown-user'}`)
    .replace(ROLE_MENTION_TOKEN_RE, (_full, id: string) => `@${resolveRole?.(id) ?? 'unknown-role'}`)
    .replace(CUSTOM_EMOJI_RE, (_full, name: string) => `:${name}:`);
}
